import { useAuth } from '@state/auth';
import { v4 } from './uuid.js';

const stashRequest = (url, reqId) => {
  if (!window.retracedApiRequests) {
    window.retracedApiRequests = [];
  }

  window.retracedApiRequests.unshift({
    url,
    reqId,
  });
  window.retracedApiRequests.splice(3);
};

// Endpoints that don't require auth and must be reachable even when the
// browser is holding a stale/expired accessToken - attaching it anyway
// makes the backend's auth pre-check 401 before the handler ever runs.
const UNAUTHENTICATED_EXACT_PATHS = new Set([
  '/authentication/request-code',
  '/authentication/login',
  '/authentication/sso-code',
  '/authentication/sso-status',
  '/authentication/oauth2-callback',
]);

// Only single-sign-on carries a dynamic path segment (the domain), so it
// needs a prefix match - the trailing slash keeps it from also matching an
// unrelated endpoint that merely starts with the same word, e.g. a
// hypothetical /authentication/single-sign-on-settings.
const UNAUTHENTICATED_PATH_PREFIXES = ['/authentication/single-sign-on/'];

const isUnauthenticatedPath = url => {
  if (typeof url !== 'string') return false;
  const pathname = url.split('?')[0];
  return (
    UNAUTHENTICATED_EXACT_PATHS.has(pathname) ||
    UNAUTHENTICATED_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))
  );
};

export const getFetchHeaders = (data, url) => {
  const retracedContext = JSON.parse(
    localStorage.getItem('retraced:context'),
  )?.state;
  const timezone = {
    'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const accessToken = retracedContext?.accessToken || null;
  const sessionToken = retracedContext?.sessionToken || null;

  const commonHeaders = data.headers || {};
  const {
    Authorization: authorizationHeader,
    'X-Session-Token': sessionTokenHeader,
  } = commonHeaders;

  // Add Ory session token if not already present in headers
  if (!sessionTokenHeader && sessionToken) {
    commonHeaders['X-Session-Token'] = sessionToken;
  }

  // Add Bearer token if not already present in headers
  if (!authorizationHeader && accessToken && !isUnauthenticatedPath(url)) {
    commonHeaders.Authorization = `Bearer ${accessToken}`;
  }

  const impersonateHeader = retracedContext?.impersonateId
    ? { 'x-retraced-impersonate': retracedContext.impersonateId }
    : {};
  const companyHeader = retracedContext?.companyId
    ? { 'x-retraced-company-id': retracedContext.companyId }
    : {};

  const xRequestId = { 'x-request-id': v4() };

  const contentTypeHeader =
    data.body !== undefined ? { 'Content-Type': 'application/json' } : {};

  return {
    ...commonHeaders,
    ...contentTypeHeader,
    ...impersonateHeader,
    ...companyHeader,
    ...xRequestId,
    ...timezone,
  };
};

export const getApiOrigin = () => {
  const uri = import.meta.env.VITE_API_SERVICE_URI;
  return uri ? new URL(uri).origin : '';
};

export const fetch = (rawUrl, data) => {
  const path = typeof rawUrl === 'string' ? rawUrl : undefined;
  const headers = getFetchHeaders(data, path);

  const options = {
    ...data,
    headers,
    credentials: 'include', //to include everything in the headers that came in with the response of the first request
  };

  const url =
    typeof rawUrl === 'string'
      ? `${import.meta.env.VITE_API_SERVICE_URI}${rawUrl}`
      : rawUrl.join('');
  return window.fetch(url, options).then(async response => {
    let data;
    let responseError = null;
    try {
      // Try to parse response body as JSON. Independant if error or not error response, because the API
      // might also send a message in case of errors.
      if (response.status === 401 && !isUnauthenticatedPath(path)) {
        // Must go through the store's own action rather than a raw
        // localStorage write: the `persist` middleware rewrites the entire
        // in-memory state back to storage on every `set()`, so a bare
        // `removeItem` here gets silently undone by the very next unrelated
        // store update, resurrecting the stale token and looping `/` <-> `/login`.
        // Skipped for unauthenticated endpoints: a 401 from one of those is
        // unrelated to any session (e.g. an invalid SSO code), so there's
        // nothing to log out of.
        useAuth.getState().logout();
      }

      if (response.ok) {
        data = await response.json();
      } else {
        data = null;
        responseError = await response.json();
      }
    } catch {
      data = null;
    }

    const reqId = response.headers.get('x-request-id');
    stashRequest(url, reqId);

    return {
      data,
      error: responseError
        ? {
            ...responseError,
            reqId,
          }
        : null,
      status: response.status,
    };
  });
};
